import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { action, autorun, computed, makeObservable, observable, reaction } from 'mobx';

import { getRequest, postRequest } from '../../services/api';
import { ldClient } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError, devWarn } from '../../utils/log';
import type { RootStore } from '../RootStore';

import { formatJackpotAmount } from './formatJackpotAmount';
import { trackJackpotWidgetOptIn, trackJackpotWidgetShown } from './jackpotAnalytics';
import { publishJackpotBadgeAttributes } from './jackpotBadgeAttributes';
import { JACKPOT_FLAG, resolveJackpotFlag, resolveJackpotMaintenance } from './resolveJackpotFlag';
import { toJackpotCoin, type JackpotCoinType, type JackpotEligibility, type JackpotWidgetDecision } from './types';

// GET /api/v1/jackpot/eligibility. The version segment is NOT written here: the axios request
// interceptor prefixes `/v1/` onto every non-`/v2` url, so spelling it out yields /v1/v1/....
const ELIGIBILITY_PATH = 'jackpot/eligibility';

/**
 * Enrolled games for the current coin — what puts the Crown Jackpot badge on a lobby tile.
 *
 * Fetched alongside eligibility rather than per tile: a lobby renders dozens of cards and each one
 * asking "am I in the jackpot?" would be dozens of requests for one answer that is the same for all
 * of them.
 */
/**
 * The widget's pacing decision — whether to render it at all, and whether it may open the popup by
 * itself. Fetched alongside eligibility because it is per coin and per player exactly like the rest,
 * and because the two answers are read by the same render pass.
 */
const WIDGET_PATH = 'jackpot/widget';

const PARTICIPATION_PATH = 'jackpot/participation';

const WINS_PATH = 'jackpot/wins';

/**
 * Progressive pots tick up continuously, so the lobby total is a live value rather than a snapshot.
 * 30s is the cheapest cadence that still reads as "moving": at 60s a 9-digit total visibly jumps
 * between refreshes, and below 30s the extra request per player per tick buys a change too small to
 * resolve on screen. One shared poll backs every jackpot surface (category row, banner, crown badges)
 * — nothing fetches per component or per tile.
 */
const REFRESH_INTERVAL_MS = 30_000;

/**
 * The contract documents the bare envelope, while player-backend wraps responses in
 * `{ data: { success, data } }`. Accept either, so the row works whichever way the proxy lands, and
 * reject anything that is not recognisably the eligibility envelope (`canSee` is the one field that
 * is always present, including on the `canSee: false` response).
 */
export function parseEligibility(response: unknown): JackpotEligibility | null {
  const body = (response as { data?: unknown })?.data;
  const candidate = ((body as { data?: unknown })?.data ?? body ?? response) as Partial<JackpotEligibility>;

  if (!candidate || typeof candidate.canSee !== 'boolean') {
    return null;
  }

  const rawCategoryIds = (candidate as { eligibleCategoryIds?: unknown }).eligibleCategoryIds;

  return {
    ...candidate,
    levels: Array.isArray(candidate.levels) ? candidate.levels : [],
    // Absent stays absent — `[]` here would read as "no row is configured" and hide the collection on
    // every environment whose core predates the field.
    ...(Array.isArray(rawCategoryIds)
      ? {
          eligibleCategoryIds: rawCategoryIds
            // `null`/`''` must be dropped BEFORE Number(), which turns both into 0 — a real category
            // id that would badge whichever row happens to carry it.
            .filter((id) => typeof id === 'number' || (typeof id === 'string' && id.trim() !== ''))
            .map(Number)
            .filter((id) => Number.isFinite(id)),
        }
      : {}),
  } as JackpotEligibility;
}

/**
 * Same either-envelope tolerance as {@link parseEligibility}.
 *
 * `show` and `jump` must both be real booleans: they are the only two fields acted on, and coercing a
 * missing one would turn "core said nothing" into a decision. Null instead means NO decision, which
 * reads as "render the widget, do not auto-open" — see {@link JackpotStore.widgetAllowsDisplay}.
 */
export function parseWidgetDecision(response: unknown): JackpotWidgetDecision | null {
  const body = (response as { data?: unknown })?.data;
  const candidate = ((body as { data?: unknown })?.data ?? body ?? response) as Partial<JackpotWidgetDecision>;

  if (typeof candidate?.show !== 'boolean' || typeof candidate?.jump !== 'boolean') {
    return null;
  }

  return candidate as JackpotWidgetDecision;
}

/**
 * Win ids out of `GET /jackpot/wins`, through the same one-or-two `data` envelopes the other jackpot
 * reads come wrapped in. Anything that is not a list of objects with a string `id` is no wins — this
 * decides whether a popup is thrown in the player's face, so a shape it does not recognise must mean
 * "nothing" rather than "something".
 */
export function parsePendingWinIds(response: unknown): string[] {
  const body = (response as { data?: unknown })?.data;
  const candidate = (body as { data?: unknown })?.data ?? body ?? response;

  if (!Array.isArray(candidate)) {
    return [];
  }

  return candidate
    .map((win) => (win as { id?: unknown })?.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

export class JackpotStore {
  private readonly rootStore: RootStore;

  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  // Monotonic request id: a coin switch restarts the poll, and the in-flight request for the
  // previous coin must not overwrite the newer one when it lands late.
  private requestId = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    /*
     * The badge gate, published to `<body>` for CSS to act on. Same pattern as UiStore's `data-dialog`.
     *
     * WHY NOT A PER-TILE READ. A lobby renders dozens of cards, and a badge that reads the store is an
     * observer per card: every coin switch and every widget-decision change re-rendered all of them,
     * to change nothing but visibility. Two attributes here mean the whole lobby, the search results
     * and every carousel react in one style recalculation, with no React work at all.
     *
     * `data-jp-coin` is the coin whose jackpot is live — a tile shows its badge only for a group on
     * that coin, since a game is usually enrolled in both. `data-jp-badges` is the global gate
     * (maintenance, widget hidden, decision not settled yet).
     */
    autorun(() => {
      publishJackpotBadgeAttributes(toJackpotCoin(this.rootStore.coinTypeStore.coinType), this.canShowGameBadge);
    });

    this.readFlag();
    ldClient?.on('ready', () => this.readFlag());
    ldClient?.on('change', (changes) => {
      if (JACKPOT_FLAG in changes) {
        this.readFlag();
      }
    });

    // The payload is per coin, so a coin-type switch invalidates it entirely — restart rather than
    // let the old total sit under the new coin icon. Auth and the flag gate the poll the same way.
    reaction(
      () => ({
        enabled: this.isEnabled,
        authorized: this.rootStore.authStore.authorized,
        coinType: toJackpotCoin(this.rootStore.coinTypeStore.coinType),
      }),
      ({ enabled, authorized }) => {
        this.stopPolling();
        this.setEligibility(null, null);
        this.resetWidgetDecision();

        if (enabled && authorized) {
          void this.fetchEligibility();
          void this.maybeOpenPendingWin();
          /*
           * Both on the tick.
           *
           * A win the player left unclaimed is offered again when they come back to the lobby
           * (`GameIframe` fires it on the way out of the game) — but a win can also land for a player
           * who is already sitting here: another device, another tab, or a `JP_WIN` the game document
           * never delivered. The poll is what finds those, and its own guards are what keep it from
           * being an interruption: nothing opens while the player is in a game or already has a dialog
           * up, and a win is only ever offered once.
           */
          this.refreshTimer = setInterval(() => {
            void this.fetchEligibility();
            void this.maybeOpenPendingWin();
          }, REFRESH_INTERVAL_MS);
        }
      },
      {
        // The data function builds a fresh object every run, so without a value comparator mobx's
        // default reference equality would re-fire (and restart the poll) on every unrelated tick.
        equals: isEqual,
        // The flag is resolved above, before this reaction exists, and a returning player can already
        // be authorized by the time the store is constructed — without this the first poll never starts.
        fireImmediately: true,
      },
    );
  }

  /** One LD read, two derived states — they must never disagree about which branch the lobby is in. */
  private readFlag() {
    this.setEnabled(resolveJackpotFlag(JACKPOT_FLAG));
    this.setInMaintenance(resolveJackpotMaintenance(JACKPOT_FLAG));
  }

  @observable
  public isEnabled = false;

  @action
  public setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  /**
   * The flag is in a maintenance window: no request is made and nothing is fetched, but the lobby still
   * renders the small widget's maintenance plate. See {@link canShowMaintenanceWidget}.
   */
  @observable
  public isInMaintenance = false;

  @action
  public setInMaintenance(maintenance: boolean) {
    this.isInMaintenance = maintenance;
  }

  @observable
  public eligibility: JackpotEligibility | null = null;

  @observable
  public widgetDecision: JackpotWidgetDecision | null = null;

  // The coin the decision was fetched for, guarded the same way as `loadedCoinType`.
  @observable
  public widgetDecisionCoinType: JackpotCoinType | null = null;

  /**
   * Whether the decision request for the CURRENT coin has come back — either with a decision or with a
   * failure. Distinct from `widgetDecision !== null`, which cannot tell "not asked yet" from "asked
   * and got nothing", and those two must render differently: see {@link widgetAllowsDisplay}.
   */
  @observable
  public widgetDecisionSettled = false;

  @action
  public setWidgetDecision(decision: JackpotWidgetDecision | null, coinType: JackpotCoinType | null) {
    this.widgetDecision = decision;
    this.widgetDecisionCoinType = coinType;
    this.widgetDecisionSettled = true;
  }

  /** Back to "not asked yet" — a coin switch invalidates the decision as completely as the payload. */
  @action
  public resetWidgetDecision() {
    this.widgetDecision = null;
    this.widgetDecisionCoinType = null;
    this.widgetDecisionSettled = false;
  }

  /**
   * Auto-open happens AT MOST ONCE per store lifetime — one lobby session.
   *
   * Core's cooldown is the real pacing, but it is measured from `last_jump_at`, which only moves once
   * the popup reports its impression. Between the jump and that write, a poll tick would still see
   * `jump: true`. This flag is what makes that race unable to reopen a popup the player just closed.
   *
   * Deliberately NOT reset on a coin switch: toggling GC/SC is not a new session, and a player
   * flipping coins should not be jumped into the popup twice.
   */
  private hasAutoOpenedPopup = false;

  // The coin WE asked for, not the coin the response echoed back — the guard below must not depend
  // on the server repeating it correctly.
  @observable
  public loadedCoinType: JackpotCoinType | null = null;

  @action
  public setEligibility(eligibility: JackpotEligibility | null, coinType: JackpotCoinType | null) {
    this.eligibility = eligibility;
    this.loadedCoinType = coinType;
  }

  /**
   * Eligibility only if it belongs to the coin the lobby currently shows. During a coin switch the
   * previous coin's payload is still in memory for a beat; rendering it would put one coin's pot
   * behind the other coin's icon.
   */
  @computed
  public get eligibilityForSelectedCoin(): JackpotEligibility | null {
    if (!this.isEnabled || !this.eligibility) {
      return null;
    }

    return this.loadedCoinType === toJackpotCoin(this.rootStore.coinTypeStore.coinType) ? this.eligibility : null;
  }

  /**
   * The single gate for every jackpot lobby surface. False on `canSee: false`, on any error, and
   * while the first fetch is in flight — callers render NOTHING in that case, no skeleton and no
   * error state, so a jackpot outage is invisible in the lobby.
   */
  @computed
  public get canSeeJackpot(): boolean {
    return this.eligibilityForSelectedCoin?.canSee === true;
  }

  /**
   * The TOP LEVEL's pot — the figure every jackpot surface prints: the category row, both widgets and
   * the badge on a game tile.
   *
   * It used to be the sum across all levels. A sum is not a prize anyone can win, and the number under
   * the crown is an advert for the biggest one on the ladder. Which level is "top" is the operator's
   * own arrangement (level association priority), resolved by core.
   */
  @computed
  public get topLevelAmount(): number | null {
    if (!this.canSeeJackpot) {
      return null;
    }

    // `totalAmount` is core's deprecated alias, read as a fallback so this works against a core that
    // predates the rename. Both carry the same value once it ships.
    const eligibility = this.eligibilityForSelectedCoin;
    const raw = eligibility?.topLevelAmount ?? eligibility?.totalAmount;

    // `Number(null)` is 0 and `Number('')` is 0 — both finite — so a nil total used to surface as a
    // pot of zero. Core sends null whenever Splash was unreachable (`amountsLive: false`), and that
    // is "unknown", not "empty": every jackpot surface must render no figure rather than a 0.
    if (isNil(raw) || raw === '') {
      return null;
    }

    const total = Number(raw);

    return Number.isFinite(total) ? total : null;
  }

  /**
   * The decision, but only if it belongs to the coin the lobby currently shows — same guard as
   * {@link eligibilityForSelectedCoin}, for the same reason.
   */
  @computed
  public get widgetDecisionForSelectedCoin(): JackpotWidgetDecision | null {
    if (!this.widgetDecision || this.widgetDecisionCoinType === null) {
      return null;
    }

    return this.widgetDecisionCoinType === toJackpotCoin(this.rootStore.coinTypeStore.coinType)
      ? this.widgetDecision
      : null;
  }

  /**
   * What core's decision says about rendering — and the difference between "not asked yet" and
   * "asked, got nothing".
   *
   * BEFORE the request settles: hidden. Rendering optimistically and correcting on arrival is what
   * made the widget appear for a beat and vanish; the request is concurrent with the eligibility one
   * that produces `totalAmount`, so the wait costs no extra round trip.
   *
   * AFTER it settles with no decision: shown. That is an outage, or a core older than the endpoint,
   * and the widget existing is the behaviour that shipped — neither should silently remove it.
   *
   * The auto-open is the opposite in both cases: it needs an explicit yes (see
   * {@link maybeAutoOpenPopup}), because a popup nobody asked for is the failure players notice.
   */
  @computed
  private get widgetAllowsDisplay(): boolean {
    if (!this.widgetDecisionSettled) {
      return false;
    }

    return this.widgetDecisionForSelectedCoin?.show ?? true;
  }

  /**
   * The pot AS PRINTED — the one string every lobby surface renders.
   *
   * Six of them show this figure: both widgets, the promo card, the nav widget, the category row
   * header and the badge on a game tile. Each used to call `toLocaleString()` itself, which is not one
   * rule but six copies of an almost-rule: it prints up to THREE fraction digits and drops trailing
   * zeros, so a live pot ticking through .5 then .567 rendered differently on each surface and changed
   * width as it moved — and none of them agreed with the popup, which formats through game-ui's own
   * helper.
   *
   * Formatting here rather than in each component is what makes that impossible to get wrong again: a
   * new surface renders this and inherits the rule. `topLevelAmount` stays a number for the code that
   * needs one — the display gate and the analytics payload, where a formatted string would be wrong.
   *
   * Empty string when there is no figure, which is the same "render nothing" every surface already
   * handles: `null` is Splash being unreachable, not a pot of zero.
   */
  @computed
  public get topLevelAmountLabel(): string {
    return formatJackpotAmount(this.topLevelAmount);
  }

  /**
   * Single gate for EVERY Crown Jackpot lobby surface — promo card, nav icon, the category row and its
   * header, and the badge on a game thumbnail. Three things have to hold: the jackpot is visible to
   * this player, its total is a usable number, and core's pacing rules allow it on screen right now.
   *
   * One gate on purpose. The row and the tile badges used to be gated on the amount alone, so a
   * display window that hid the widget still left a Crown Jackpot collection and badged thumbnails
   * advertising a jackpot with no way into it. Whatever `jackpot_widget_config` decides, the whole
   * feature appears and disappears together.
   */
  @computed
  public get canShowWidget(): boolean {
    return this.topLevelAmount !== null && this.widgetAllowsDisplay;
  }

  /**
   * The maintenance plate, which is the ONE thing a maintenance window renders.
   *
   * Deliberately independent of everything the live widget needs: in maintenance the flag is not on, so
   * the store never polls, there is no eligibility payload, no amount and no widget decision. Gating
   * this on any of them would render nothing, which is the state this exists to replace.
   */
  @computed
  public get canShowMaintenanceWidget(): boolean {
    return this.isInMaintenance;
  }

  /**
   * Gate for the lobby jackpot collection — the ROW, not just its header.
   *
   * Deliberately the SAME value as {@link canShowWidget}, kept as its own name because the row is its
   * own surface and callers read better for it. A row advertising a jackpot the player has no widget
   * to open is the state this now prevents; the header's own render guard reads this too, or the row
   * appears with an empty amount.
   */
  @computed
  public get canShowCategoryRow(): boolean {
    return this.canShowWidget;
  }

  /**
   * Gate for the Crown Jackpot badge on a lobby game thumbnail.
   *
   * Same value again: a badged tile is an advert for the jackpot, so it follows the widget rather than
   * outliving it. WHICH games carry a badge is not asked here — it arrives per game on the games
   * listing (`Game.jackpots`), because a group can enrol ~50k brand games and no id set that size
   * belongs on a lobby load.
   *
   * Published to `<body>` rather than read per tile — see the autorun in the constructor.
   */
  @computed
  public get canShowGameBadge(): boolean {
    return this.canShowWidget;
  }

  /**
   * Lobby rows configured as jackpot collections (KRA-303) — the CATEGORY + INCLUDE ids from
   * `splash_jackpot.jackpot_eligible_slot_rule`, as core sent them.
   *
   * `undefined` distinguishes "core did not send the field" (older build; `isJackpotCategory` then
   * falls back to the row name) from `[]`, which is core stating that NO row is configured.
   */
  @computed
  public get eligibleCategoryIds(): number[] | undefined {
    return this.eligibilityForSelectedCoin?.eligibleCategoryIds;
  }

  /**
   * Coins whose `lobby_widget` impression has already been reported. Not observable — nothing renders
   * from it.
   */
  private readonly widgetImpressionReported = new Set<JackpotCoinType>();

  /**
   * Report that the lobby widget was drawn, at most once per coin.
   *
   * Deduped HERE rather than per component because three surfaces can be on the lobby at the same
   * time (promo card, rail widget, nav icon) and they are one exposure to the player, not three. A
   * coin switch is a new exposure: the pool and the join state are per coin.
   *
   * The pool figure is whatever is loaded at this moment — omitted while the overview is still in
   * flight. Waiting for it would mean losing the impression entirely whenever Splash is unreachable,
   * which is exactly when knowing the widget was still shown matters.
   */
  public reportWidgetShown(): void {
    const coinType = toJackpotCoin(this.rootStore.coinTypeStore.coinType);

    if (this.widgetImpressionReported.has(coinType)) {
      return;
    }

    this.widgetImpressionReported.add(coinType);
    trackJackpotWidgetShown(coinType, this.isOptedIn, this.topLevelAmount);
  }

  /** Current opt-in state for the coin the lobby is showing — drives OPT IN vs the PLAY NOW CTA. */
  @computed
  public get isOptedIn(): boolean {
    return this.eligibilityForSelectedCoin?.participate === true;
  }

  /**
   * CTA label gate for the promo card (FE_DESIGN_SPEC §7.4 variant B): `OPT IN` while the player is
   * out, the neutral `PLAY NOW` treatment once they are in — or when they are barred from joining at
   * all, since `canParticipate` is the segment/jurisdiction check and offering OPT IN to a player
   * the popup will refuse reads as a broken button.
   *
   * Also the gate on {@link optIn}: the promo card's CTA now joins in place rather than opening the
   * popup to do it, so the label and the action are one decision. The other two surfaces (the round
   * nav icon, the card body) still only open the popup — they carry no CTA of their own.
   */
  @computed
  public get showOptInCta(): boolean {
    const eligibility = this.eligibilityForSelectedCoin;

    return eligibility?.canParticipate === true && eligibility.participate === false;
  }

  /**
   * The widget's pacing decision, and the auto-open it may authorise.
   *
   * Quiet on failure like the games list: no decision leaves the widget rendering as it does today
   * (see {@link widgetAllowsDisplay}) rather than blanking a lobby surface on a core blip. Shares
   * `requestId` with eligibility so a coin switch discards a late response the same way.
   */
  private async fetchWidgetDecision(coinType: JackpotCoinType, requestId: number): Promise<void> {
    try {
      const response = await getRequest<ApiResponse<JackpotWidgetDecision>>(WIDGET_PATH, { coinType });

      if (requestId !== this.requestId) {
        return;
      }

      const decision = parseWidgetDecision(response);

      this.setWidgetDecision(decision, decision ? coinType : null);

      /*
       * The widget disappearing a moment after the lobby paints is what this pacing looks like when it
       * hides something, and from the outside it is indistinguishable from a bug. `reason` is core's
       * own account of which rule did it — HIDDEN_BY_CONFIG is an operator's off switch (fix it in
       * admin), OUTSIDE_DISPLAY_WINDOW is the feature working, NOT_CONFIGURED can no longer hide.
       */
      if (decision && !decision.show) {
        devWarn('JackpotStore: jackpot widget hidden by core', decision.reason, decision.segmentKey ?? '');
      }

      this.maybeAutoOpenPopup();
    } catch (error) {
      devError('JackpotStore: fetchWidgetDecision error', getErrorMessage(error));

      if (requestId === this.requestId) {
        // Null, not a hide: an outage must not be the thing that removes the widget.
        this.setWidgetDecision(null, null);
      }
    }
  }

  /**
   * Opens the jackpot popup on core's say-so — the widget "jump".
   *
   * Three conditions, and each one is load-bearing:
   *   - core said `jump`. This side never decides to jump on its own.
   *   - the widget is actually on screen (`canShowWidget`). Opening a popup for a surface the player
   *     cannot see would arrive out of nowhere.
   *   - no dialog is already open. Stacking this over a cash offer, a game, or the popup itself would
   *     interrupt something the player chose.
   *
   * Opening it makes game-ui report the impression, which is what moves `last_jump_at` and starts
   * core's cooldown — so the write that paces this is a consequence of the open, not something this
   * method has to do.
   */
  private maybeAutoOpenPopup(): void {
    if (this.hasAutoOpenedPopup || !this.widgetDecisionForSelectedCoin?.jump || !this.canShowWidget) {
      return;
    }

    if (this.rootStore.uiStore.isDialogOpen) {
      return;
    }

    this.hasAutoOpenedPopup = true;
    // Nobody tapped — see the `trigger` note in `openJackpotDialog`.
    this.rootStore.gameUiStore.openJackpotDialog({ trigger: 'auto' });
  }

  /**
   * Wins this lobby has already opened the ceremony for, so a player who closes it without finishing
   * is not handed the same popup on every return to the lobby. Session-lived on purpose: the win is
   * still PENDING server-side and the next session — or the in-slot banner — offers it again.
   */
  private autoOpenedWinIds = new Set<string>();

  /**
   * An unclaimed jackpot win opens its ceremony when the player comes back to the lobby.
   *
   * A win is decided by Splash mid-spin and lands on the in-slot banner as `CLAIM`, which the player
   * can simply walk away from — the money then sits PENDING with nothing in the lobby saying so. The
   * lobby has no win surface of its own: the ceremony lives in game-ui, so what this does is open it in
   * WIN-ONLY mode, where that document renders nothing itself and the ceremony plays over the lobby
   * (with no launch context it reveals the win immediately rather than waiting for a `CLAIM` tap that
   * has no banner to come from). The popup is deliberately not part of this.
   *
   * Guards, each load-bearing:
   *   - not in a game. There the in-slot banner owns the win, and a dialog dropped over a running slot
   *     is the thing the `CLAIM`-first flow exists to avoid.
   *   - no dialog already open — same rule as the widget jump: stacking over something the player
   *     chose is worse than waiting for the next return.
   *   - once per win. Re-checked AFTER the request, because the await is long enough for a dialog to
   *     have opened in the meantime, and a win is only marked seen once it has actually been shown.
   */
  public async maybeOpenPendingWin(): Promise<void> {
    if (!this.isEnabled || !this.rootStore.authStore.authorized) {
      return;
    }

    // Read at call time rather than observed: this fires on a route change, and the SPA's path is
    // what "in a game" means here (`/games/:id`, the same prefix GameIframe keys its own visibility on).
    if (window.location.pathname.startsWith('/games') || this.rootStore.uiStore.isDialogOpen) {
      return;
    }

    try {
      const response = await getRequest<ApiResponse<{ id?: string }[]>>(WINS_PATH, { status: 'PENDING' });
      const pending = parsePendingWinIds(response).filter((id) => !this.autoOpenedWinIds.has(id));

      if (!pending.length || this.rootStore.uiStore.isDialogOpen) {
        return;
      }

      pending.forEach((id) => this.autoOpenedWinIds.add(id));
      // The WIN, not the popup: `openJackpotWinDialog` opens game-ui with `winOnly`, so the ceremony is
      // the only thing that appears. A player who just won is not asked to read the ladder first.
      this.rootStore.gameUiStore.openJackpotWinDialog();
    } catch (error) {
      // A read. The win stays PENDING and the next lobby entry — or the next game launch — finds it.
      devError('JackpotStore: pending-win check failed', getErrorMessage(error));
    }
  }

  /** A widget opt-in is in flight — the CTA is held rather than allowed to fire twice. */
  @observable
  public optInPending = false;

  @action
  private setOptInPending(pending: boolean) {
    this.optInPending = pending;
  }

  /**
   * OPT IN from the lobby widget's CTA, without opening the popup.
   *
   * The popup used to be the only caller of `POST /api/v1/jackpot/participation` (FE_DESIGN_SPEC
   * §7.4) so that a player saw the levels, the contribution cost and How-It-Works before joining.
   * That is no longer the flow for the promo card: its CTA already reads `OPT IN`, and it now does
   * what it says. The popup remains the surface for everything else — opting OUT, the ladder, the
   * eligible games — and remains reachable from the card body.
   *
   * Gated on {@link showOptInCta}, so it cannot fire for a player who is already in or who
   * `canParticipate: false` would have the server refuse anyway.
   *
   * The CTA does not flip on the local write: `fetchEligibility` re-reads the row afterwards and the
   * server is what the label follows. One extra round trip, and no chance of a lobby claiming a join
   * that core did not record.
   *
   * On failure the popup opens instead. It is the surface with the retry, the opt-out and the copy
   * explaining what went wrong; silently leaving the button as it was would read as a dead tap. Sent
   * as `auto` so it does not report a second click the player never made.
   */
  public async optIn(): Promise<boolean> {
    const jackpotId = this.eligibilityForSelectedCoin?.jackpotId;

    if (!this.showOptInCta || this.optInPending || !jackpotId) {
      return false;
    }

    this.setOptInPending(true);
    trackJackpotWidgetOptIn(this.loadedCoinType ?? null);

    try {
      await postRequest(PARTICIPATION_PATH, { jackpotId, participate: true });
      await this.fetchEligibility();

      return true;
    } catch (error) {
      devError('JackpotStore: widget opt-in failed', getErrorMessage(error));
      this.rootStore.gameUiStore.openJackpotDialog({ trigger: 'auto', source: 'cta' });

      return false;
    } finally {
      this.setOptInPending(false);
    }
  }

  public async fetchEligibility(): Promise<void> {
    if (!this.isEnabled || !this.rootStore.authStore.authorized) {
      return;
    }

    const coinType = toJackpotCoin(this.rootStore.coinTypeStore.coinType);
    const requestId = ++this.requestId;

    /*
     * Started alongside eligibility, not chained behind it. The widget renders only once BOTH have
     * landed (see `widgetAllowsDisplay`), so chaining would delay it by a whole round trip — and
     * rendering before the decision arrives is what made it flash in and disappear.
     */
    void this.fetchWidgetDecision(coinType, requestId);

    try {
      const response = await getRequest<ApiResponse<JackpotEligibility>>(ELIGIBILITY_PATH, { coinType });

      if (requestId === this.requestId) {
        this.setEligibility(parseEligibility(response), coinType);
      }
    } catch (error) {
      // The endpoint never 404s for "no jackpot" — it answers `canSee: false` — so a throw here is a
      // real outage. Degrade to no data, which reads as canSee false and renders nothing.
      devError('JackpotStore: fetchEligibility error', getErrorMessage(error));

      if (requestId === this.requestId) {
        this.setEligibility(null, coinType);
        // The decision request is independent and settles itself; a failure here is not one there.
      }
    }
  }

  public stopPolling() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
